// src/components/Sidebar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getNavigationForRole, getRoleDisplayName } from '../config/roles';
import logoIcon from '../assets/icon.png';

const Sidebar = ({ activeTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem('sidebar-collapsed') === 'true');

  // Get navigation items based on user role
  const navItems = getNavigationForRole(currentUser?.role);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const setSidebarCollapsed = (nextValue) => {
    setCollapsed(nextValue);
    sessionStorage.setItem('sidebar-collapsed', String(nextValue));
  };

  const handleNavigation = (path) => {
    setSidebarCollapsed(true);
    navigate(path);
  };

  // Get current path without leading slash
  const currentPath = location.pathname.slice(1);

  return (
    <aside className={`${collapsed ? 'w-16 px-2' : 'w-64 px-4'} min-h-screen bg-brand-950 text-white flex flex-col py-6 transition-all duration-200 flex-shrink-0`}>
      <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} mb-8`}>
        <img src={logoIcon} alt="Integrity MHC" className="w-11 h-11 rounded-full flex-shrink-0" />
        {!collapsed && <div className="leading-tight">
          <div className="text-lg font-bold">Integrity MHC</div>
          <div className="text-xs text-brand-300">Mental Health &amp; Coaching</div>
        </div>}
      </div>
      <button
        type="button"
        onClick={() => setSidebarCollapsed(!collapsed)}
        className="mb-5 flex w-full items-center justify-center rounded-lg border border-brand-800 px-2 py-2 text-brand-100 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-400"
        aria-label={collapsed ? 'Open navigation' : 'Collapse navigation'}
        title={collapsed ? 'Open navigation' : 'Collapse navigation'}
      >
        <svg className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>
      <nav className="flex-1">
        {!collapsed && <ul className="space-y-2">
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => handleNavigation(item.path)}
                className={`w-full text-left px-4 py-2 rounded transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                  currentPath === item.id ? 'bg-brand-800' : 'hover:bg-brand-800'
                }`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>}
      </nav>

      {/* User Profile and Logout Section - Always visible */}
      <div className="mt-auto border-t border-brand-800 pt-4">
        {!collapsed && <div className="px-4 py-2 text-sm text-brand-100">
          {currentUser?.name || currentUser?.email}
          {currentUser?.role && (
            <div className="text-xs text-brand-300">
              {getRoleDisplayName(currentUser.role)}
            </div>
          )}
        </div>}
        <button
          onClick={handleLogout}
          className={`w-full ${collapsed ? 'text-center px-1' : 'text-left px-4'} py-2 rounded transition-colors font-medium hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-400 text-red-400 hover:text-red-300`}
          title={collapsed ? 'Logout' : undefined}
        >
          {collapsed ? '↪' : 'Logout'}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
